import React, { useMemo } from 'react'
import CustomSearchBar from '../../searchbar'
import SelectFilter from '@/src/components/Filter-Component/components/SelectFilter'
import { ArrowIcon, CalanderIcon, CategoryIcon, NewDollorIcon } from '@/src/assets/icons'
import { useAnnouncementStore } from '@/src/types/announcement'
import { useStore } from '@/src/types/store'
import { Typography, useMediaQuery } from '@mui/material'

interface NewListContainerProps {
  handleSearch: (val: any) => void
  years: string[]
  selectedTabs: string[]
  handleClick: (val: string, category?: boolean) => void
  handleYearChange: (val: string[]) => void
  tags: string[]
  tagsList: string[]
  handleTagsChange: (val: string[]) => void
  handleClearAll: () => void
  /** Persisted search term used to rehydrate the search input on mount. */
  searchKey?: string
}

interface LabelOption {
  id: string
  label: string
  shortLabel?: string
  shortIcon?: React.ReactNode
  key: string | boolean
}

const NewFilterContainer = ({
  handleSearch,
  selectedTabs,
  handleClick,
  years,
  handleYearChange,
  tags,
  tagsList,
  handleTagsChange,
  handleClearAll,
  searchKey
}: NewListContainerProps) => {
  const { HeaderCategories } = useAnnouncementStore()
  const { annYearsList } = useStore()
  // Below this width the single-row layout starts wrapping; shorten labels so
  // everything fits. 1500px catches 1920×1080 at 150% Windows scaling
  // (effective CSS width 1280px) while leaving full labels on bigger displays.
  const isCompact = useMediaQuery('(max-width:1500px)')

  const LabelButtons: LabelOption[] = useMemo(
    () => [
      { label: 'All', id: 'All', key: true },
      { label: 'Company Reports', shortLabel: 'Reports', id: 'Company Reports', key: 'annual_report' },
      {
        label: 'Price Sensitive',
        shortIcon: <NewDollorIcon className="w-4 h-4" />,
        id: 'Price Sensitive',
        key: true
      },
      { label: 'Presentations', id: 'Presentation', key: 'presentation' }
    ],
    []
  )

  const visibleLabels = useMemo(
    () =>
      LabelButtons.filter(val =>
        val?.key === true ? true : (HeaderCategories as any)[val?.key as string] > 0
      ),
    [LabelButtons, HeaderCategories]
  )

  const yearOptions = useMemo(
    () => annYearsList.map((y: any) => ({ id: String(y), name: y, value: y })),
    [annYearsList]
  )

  const tagOptions = useMemo(
    () => tagsList.map(tag => ({ id: tag, name: tag, value: tag })),
    [tagsList]
  )

  return (
    <div className="mb-6 mt-1 flex flex-wrap items-center gap-3 w-full max-w-full">
      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap basis-full sm:basis-auto">
        {visibleLabels.map(label => (
          <button
            key={label.id}
            onClick={() => handleClick(label.id)}
            className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs font-medium border transition-colors whitespace-nowrap ${
              selectedTabs.includes(label.id)
                ? 'bg-[#07004D] text-white border-[#07004D] [&_path]:fill-white'
                : 'bg-white text-secondary-main87 border-[#0000001F] hover:border-[#0000003D]'
            }`}
          >
            {label.shortIcon ? (
              <>
                <span className="hidden sm:inline">{label.label}</span>
                <span className="sm:hidden inline-flex items-center">
                  {label.shortIcon}
                  <span className="sr-only">{label.label}</span>
                </span>
              </>
            ) : label.shortLabel ? (
              <>
                <span className="hidden sm:inline">{label.label}</span>
                <span className="sm:hidden">{label.shortLabel}</span>
              </>
            ) : (
              label.label
            )}
          </button>
        ))}
      </div>

      <SelectFilter
        options={tagOptions}
        label="Tags"
        leftIcon={<CategoryIcon className="w-5 h-5 fill-primary-main" />}
        rightIcon={<ArrowIcon className="w-[12px] h-[12px] rotate-90 fill-primary-main" />}
        minWidth="140px"
        menuMaxHeight={320}
        wrapOptionText
        multiple
        emptyText="No tags yet"
        defaultValue={tags}
        onSelectionChange={(ids: string[]) => handleTagsChange((ids ?? []).map(String))}
      />

      <SelectFilter
        options={yearOptions}
        label="Year"
        leftIcon={<CalanderIcon className="w-5 h-5 fill-primary-main" />}
        rightIcon={<ArrowIcon className="w-[12px] h-[12px] rotate-90 fill-primary-main" />}
        minWidth="160px"
        multiple
        defaultValue={years}
        onSelectionChange={(ids: any[]) => handleYearChange(ids.map(String))}
      />

      {/* Spacer: only present on sm+, absorbs leftover row space so Clear+Search
          right-align without us giving Search flex-grow (which would defeat that). */}
      <div className="hidden sm:block sm:grow" />

      <Typography
        variant="subtitle2"
        color="primary.main"
        className="underline cursor-pointer text-xxs font-medium whitespace-nowrap shrink-0"
        onClick={handleClearAll}
      >
        {isCompact ? 'Clear' : 'Clear all filters'}
      </Typography>

      <div className="flex-1 min-w-0 basis-full sm:basis-[140px] sm:max-w-[320px]">
        <CustomSearchBar
          fluid
          onSearch={handleSearch}
          defaultValue={searchKey}
          placeholder={isCompact ? 'Search' : 'Search announcements'}
          classes="!rounded-full text-black-800 text-2xs !h-[44px]"
        />
      </div>
    </div>
  )
}

export default NewFilterContainer
